import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Click-to-copy email link.
 * - On click: copies the email to the clipboard and shows a brief "Copied" confirmation.
 * - Keeps href="mailto:..." so screen readers + browser "Copy email address" still work.
 * - Falls back to a hidden <input> + execCommand for older browsers without navigator.clipboard.
 */
const CopyEmailLink = ({ email, className, testId }) => {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleClick = useCallback(
    async (e) => {
      e.preventDefault();
      let ok = false;
      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(email);
          ok = true;
        } else {
          const input = document.createElement('input');
          input.value = email;
          input.setAttribute('readonly', '');
          input.style.position = 'absolute';
          input.style.left = '-9999px';
          document.body.appendChild(input);
          input.select();
          ok = document.execCommand('copy');
          document.body.removeChild(input);
        }
      } catch {
        ok = false;
      }
      if (ok) {
        setCopied(true);
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setCopied(false), 2200);
      }
    },
    [email]
  );

  return (
    <a
      href={`mailto:${email}`}
      onClick={handleClick}
      className={className}
      data-testid={testId || 'copy-email-link'}
      aria-live="polite"
      title={t('Klik om te kopiëren', 'Click to copy')}
    >
      {copied ? t('✓ Gekopieerd', '✓ Copied') : email}
    </a>
  );
};

export default CopyEmailLink;
